import { useState, useEffect, useCallback } from 'react';
import { ExternalLink, MapPin, Calendar, Hash, Loader2, Plus, Users, FileText, DollarSign, Eye, RefreshCw, Star } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import PackageCard from './PackageCard';
import NotesTasksSection from './NotesTasksSection';
import AddNoteModal from './AddNoteModal';
import AddTaskModal from './AddTaskModal';
import { AddToCrmModal } from '../AddToCrmModal';
import { viewQuotePdf } from '../../utils/viewQuotePdf';
import AssignProjectToUsersModal from './AssignProjectToUsersModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENCON_ORANGE = '#ee7203';

function fmt(val) {
  if (!val) return '—';
  const n = parseFloat(val);
  if (!isNaN(n)) return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  return val;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function QuoteRow({ quote, index }) {
  return (
    <div className={`px-5 py-3.5 flex items-start gap-4 ${index > 0 ? 'border-t border-gray-100' : ''}`}>
      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <span className="text-sm font-semibold text-gray-800">{quote.subject || `Quote ${index + 1}`}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {quote.stage && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{quote.stage}</span>
            )}
            {quote.value && (
              <span className="text-sm font-bold" style={{ color: ENCON_ORANGE }}>
                £{Number(quote.value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {quote.supplier && <span className="text-[12px] text-gray-500">Supplier: {quote.supplier}</span>}
          {quote.contractor_package_name && <span className="text-[11px] text-gray-400">Package: {quote.contractor_package_name}</span>}
          {quote.sub_contractor_package_name && <span className="text-[11px] text-gray-400">Package: {quote.sub_contractor_package_name}</span>}
        </div>
        {quote.description && (
          <p className="text-[12px] text-gray-400 mt-0.5 line-clamp-2 leading-snug">{quote.description}</p>
        )}
        {quote.has_pdf && (
          <button
            onClick={() => viewQuotePdf(quote.id, quote.subject)}
            className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold rounded px-2.5 py-1 hover:opacity-80 transition-opacity print:hidden"
            style={{ backgroundColor: '#fff3e6', color: ENCON_ORANGE }}
            data-testid={`pt-project-quote-pdf-${quote.id}`}
            title="View PDF"
          >
            <Eye className="w-3 h-3" /> View PDF
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProjectDetailPanel({ project, user, onPackagesLoaded }) {
  const [packages, setPackages] = useState(null);
  const [pkgLoading, setPkgLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [addNoteTarget, setAddNoteTarget] = useState(null);
  const [addTaskTarget, setAddTaskTarget] = useState(null);
  const [showAddQuote, setShowAddQuote] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [refreshNotesTick, setRefreshNotesTick] = useState(0);
  const [refreshTasksTick, setRefreshTasksTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isSavedToMyProjects, setIsSavedToMyProjects] = useState(false);
  const [savingMyProjects, setSavingMyProjects] = useState(false);

  const isManager = user?.role === 'super_admin' || user?.is_crm_manager || user?.is_team_manager;

  // Check if this project is in the current user's My Projects list
  useEffect(() => {
    api.get(`${API}/project-tracking/my-assigned`)
      .then(r => {
        const ids = (r.data.projects || []).map(p => p.project_id);
        setIsSavedToMyProjects(ids.includes(project.id));
      })
      .catch(() => {});
  }, [project.id]); // eslint-disable-line

  const handleToggleMyProjects = useCallback(async () => {
    setSavingMyProjects(true);
    try {
      if (isSavedToMyProjects) {
        await api.delete(`${API}/project-tracking/my-assigned/${project.id}`);
        setIsSavedToMyProjects(false);
        toast.success('Removed from My Projects');
      } else {
        await api.post(`${API}/project-tracking/my-assigned`, {
          project_id: project.id,
          project_name: project.name || '',
          project_stage: project.stage || '',
          project_value: project.encon_value || project.barbour_value || null,
          crm_url: project.crm_url || '',
        });
        setIsSavedToMyProjects(true);
        toast.success('Added to My Projects');
      }
    } catch (e) {
      toast.error('Failed to update My Projects');
    } finally {
      setSavingMyProjects(false);
    }
  }, [isSavedToMyProjects, project]); // eslint-disable-line

  const fetchPackages = useCallback(async () => {
    setPkgLoading(true);
    try {
      const res = await api.get(`${API}/zoho/project/${project.id}/packages`);
      setPackages(res.data);
      onPackagesLoaded?.(project.id, res.data);
    } catch (e) {
      toast.error('Failed to load project packages');
      setPackages({ contractor_packages: [], sub_contractor_packages: [] });
    } finally {
      setPkgLoading(false);
    }
  }, [project.id]); // eslint-disable-line

  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const res = await api.get(`${API}/zoho/project/${project.id}/quotes`);
      setQuotes(res.data.quotes || []);
    } catch {
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, [project.id]); // eslint-disable-line

  useEffect(() => { fetchPackages(); fetchQuotes(); }, [fetchPackages, fetchQuotes]);

  const handleNoteAdded = () => { setAddNoteTarget(null); setRefreshNotesTick(t => t + 1); toast.success('Note added'); };
  const handleTaskAdded = () => { setAddTaskTarget(null); setRefreshTasksTick(t => t + 1); toast.success('Task added'); };
  const handleQuoteSaved = () => { setShowAddQuote(false); fetchPackages(); fetchQuotes(); toast.success('Quote saved'); };
  const handleContractorSaved = () => { setShowAddContractor(false); fetchPackages(); };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchPackages(), fetchQuotes()]);
    setRefreshNotesTick(t => t + 1);
    setRefreshTasksTick(t => t + 1);
    setRefreshing(false);
    toast.success('Project refreshed');
  }, [fetchPackages, fetchQuotes]);

  const allPkgs = [
    ...(packages?.contractor_packages || []).map(p => ({ ...p, _module: 'Contractor_Packages' })),
    ...(packages?.sub_contractor_packages || []).map(p => ({ ...p, _module: 'Sub_Contractor_Packages' })),
  ];

  const initialProject = {
    id: project.id,
    deal_name: project.name,
    barbour_project_id: project.barbour_project_id,
    postcode: project.postcode,
    stage: project.stage,
  };

  return (
    <div className="project-detail-panel" data-testid="pt-project-detail" data-project-id={project.id}>

      {/* ── Project info card — sticks to top when scrolled ─── */}
      <div className="sticky top-0 z-30 project-info-card shadow-sm">

        {/* Orange title band */}
        <div className="px-6 py-4 flex items-center justify-between gap-4" style={{ backgroundColor: ENCON_ORANGE }}>
          <div className="min-w-0">
            <h2 className="text-xl font-bold leading-tight text-white truncate">{project.name}</h2>
            {project.barbour_project_name && project.barbour_project_name !== project.name && (
              <p className="text-sm text-white/75 mt-0.5 truncate">{project.barbour_project_name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity print:hidden disabled:opacity-60"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              data-testid="pt-refresh-btn"
              title="Refresh packages, quotes, notes and tasks">
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {project.crm_url && (
              <a href={project.crm_url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity print:hidden"
                style={{ backgroundColor: '#2563eb' }}>
                <ExternalLink className="w-3 h-3" /> Open in Zoho
              </a>
            )}
            <button onClick={() => setShowAddContractor(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity print:hidden"
              style={{ backgroundColor: '#6b7280' }}
              data-testid="pt-add-contractor-btn">
              <Users className="w-3 h-3" /> Add Contractor
            </button>
            <button onClick={() => setShowAddQuote(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity print:hidden"
              style={{ backgroundColor: '#16a34a' }}
              data-testid="pt-add-quote-btn">
              <Plus className="w-3 h-3" /> Add Quote
            </button>
            {isManager && (
              <button onClick={() => setShowAssignModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity print:hidden"
                style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                data-testid="pt-assign-to-users-btn">
                <Users className="w-3 h-3" /> Assign to Users
              </button>
            )}
            {/* Any user can save/remove this project from their personal My Projects tab */}
            <button
              onClick={handleToggleMyProjects}
              disabled={savingMyProjects}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80 transition-opacity print:hidden disabled:opacity-60"
              style={{
                backgroundColor: isSavedToMyProjects ? '#fef3c7' : 'rgba(255,255,255,0.25)',
                color: isSavedToMyProjects ? '#92400e' : '#fff',
                border: isSavedToMyProjects ? '1px solid #fcd34d' : 'none',
              }}
              data-testid="pt-my-projects-btn"
              title={isSavedToMyProjects ? 'Remove from My Projects' : 'Save to My Projects'}
            >
              {savingMyProjects
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Star className={`w-3 h-3 ${isSavedToMyProjects ? 'fill-yellow-500' : ''}`} />
              }
              {isSavedToMyProjects ? 'My Projects' : 'Save to Mine'}
            </button>
          </div>
        </div>

        {/* White stats band */}
        <div className="bg-white border-b-2 border-gray-200 px-6 py-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: 'Stage', value: project.stage || '—' },
              { label: 'Value', value: fmt(project.encon_value || project.barbour_value), orange: true },
              { label: 'Address', icon: <MapPin className="w-3 h-3 text-gray-400" />, value: [project.town, project.postcode].filter(Boolean).join(', ') || '—' },
              { label: 'Closing', icon: <Calendar className="w-3 h-3 text-gray-400" />, value: formatDate(project.closing_date) },
              { label: 'Barbour ID', icon: <Hash className="w-3 h-3 text-gray-400" />, value: project.barbour_project_id || '—', barbourLink: project.barbour_project_id ? `https://app.barbour-abi.com/app/project/${project.barbour_project_id}` : null },
            ].map(({ label, value, icon, orange, barbourLink }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
                {barbourLink ? (
                  <a
                    href={barbourLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-semibold flex items-center gap-1 hover:underline"
                    style={{ color: ENCON_ORANGE }}
                    data-testid="pt-barbour-id-link"
                  >
                    {icon}{value} <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : (
                  <span className={`text-sm font-semibold flex items-center gap-1 ${orange ? '' : 'text-gray-800'}`}
                    style={orange ? { color: ENCON_ORANGE } : {}}>
                    {icon}{value}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Scrollable content below the sticky header ─────── */}
      <div className="max-w-screen-xl mx-auto px-4 py-6 space-y-6">

      {/* ── Packages ──────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-800">Packages ({allPkgs.length})</h3>
          <button onClick={() => setShowAddContractor(true)}
            className="flex items-center gap-1 text-xs font-semibold hover:opacity-80 print:hidden"
            style={{ color: ENCON_ORANGE }}>
            <Plus className="w-3.5 h-3.5" /> Add Contractor / Package
          </button>
        </div>
        {pkgLoading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-6">
            <Loader2 className="w-4 h-4 animate-spin" />Loading packages…
          </div>
        ) : allPkgs.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center text-gray-400 text-sm">
            No packages yet — add a contractor to get started
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {allPkgs.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                project={project}
                onAddNote={() => setAddNoteTarget({
                  type: 'package', module: pkg._module, id: pkg.id,
                  name: pkg.contractor_account_name || pkg.sub_contractor_account_name || pkg.package_type || 'Package',
                })}
                onAddTask={() => setAddTaskTarget({
                  type: 'package', module: pkg._module, id: pkg.id,
                  name: pkg.contractor_account_name || pkg.sub_contractor_account_name || pkg.package_type || 'Package',
                })}
                onAddQuote={() => setShowAddQuote(true)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Quotes (standalone — not linked to any package) ─── */}
      {(() => {
        const standaloneQuotes = quotes.filter(q => !q.contractor_package_id && !q.sub_contractor_package_id);
        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <DollarSign className="w-4 h-4" style={{ color: ENCON_ORANGE }} />
                Quotes {!quotesLoading && standaloneQuotes.length > 0 && `(${standaloneQuotes.length})`}
              </h3>
              <button onClick={() => setShowAddQuote(true)}
                className="flex items-center gap-1 text-xs font-semibold hover:opacity-80 print:hidden"
                style={{ color: ENCON_ORANGE }}>
                <Plus className="w-3.5 h-3.5" /> Add Quote
              </button>
            </div>
            {quotesLoading ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" />Loading quotes…
              </div>
            ) : standaloneQuotes.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-gray-200 p-5 text-center text-gray-400 text-sm">
                No standalone quotes — all quotes are linked to packages above
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-gray-200 shadow overflow-hidden">
                {standaloneQuotes.map((q, i) => <QuoteRow key={q.id} quote={q} index={i} />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Project-level notes & tasks ───────────────────────── */}
      <NotesTasksSection
        title="Project Notes & Tasks"
        fetchNotesUrl={`${API}/zoho/project/${project.id}/notes`}
        fetchTasksUrl={`${API}/zoho/project/${project.id}/tasks`}
        onAddNote={() => setAddNoteTarget({ type: 'project', id: project.id, name: project.name })}
        onAddTask={() => setAddTaskTarget({ type: 'project', module: 'Potentials', id: project.id, name: project.name })}
        refreshNotesTick={refreshNotesTick}
        refreshTasksTick={refreshTasksTick}
      />

      </div>{/* end scrollable content */}

      {/* ── Modals ────────────────────────────────────────────── */}
      {addNoteTarget && <AddNoteModal target={addNoteTarget} onClose={() => setAddNoteTarget(null)} onSaved={handleNoteAdded} />}
      {addTaskTarget && <AddTaskModal target={addTaskTarget} projectId={project.id} onClose={() => setAddTaskTarget(null)} onSaved={handleTaskAdded} />}
      {showAddQuote && (
        <AddToCrmModal isOpen={showAddQuote} onClose={handleQuoteSaved} fastTrack initialMode="project" initialProject={initialProject} zIndex={100} />
      )}
      {showAddContractor && (
        <AddToCrmModal isOpen={showAddContractor} onClose={handleContractorSaved} fastTrack initialMode="project" initialProject={initialProject} zIndex={100} />
      )}
      {showAssignModal && (
        <AssignProjectToUsersModal
          project={project}
          user={user}
          onClose={() => setShowAssignModal(false)}
          onSaved={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );
}
